import React, {useDebugValue} from 'react';

import classNames from 'classnames';

import {useSearchFormContext} from '../../../hooks';

import {
    shouldDestinationFieldAutoProgressOnSearchSelected,
    showDestinationInputFieldError,
    showDestinationIcon
} from '../../../utils';

import {SearchIcon} from '../../SearchIcon';

const classNamespace = 'search-form';
const fieldClassName = `${classNamespace}__field`;
const inputFieldClassName = `${classNamespace}__input-field`;
const inputFieldErrorClassName = `${inputFieldClassName}--error`;
const detailsFieldClassName = `${classNamespace}__details-field`;
const detailsInputFieldClassName = `${classNamespace}__details-input-field`;
const destinationDetailsInputFieldClassName = `${classNamespace}__destination-details-input-field`;

// eslint-disable-next-line complexity
function useComponent() {
    const {
        systemOptions,
        systemOptions: {
            site,
            baseUrl,
            appPrefix,
            locale
        },
        mediaSettings,
        mediaSettings: {
            isMobile
        },
        abacusStore,
        formOptions,
        formSettings,
        destinationActions: {
            setIsTypeaheadInputFocus,
            setAllDestinationValues
        },
        destinationEvents: {
            onBlur,
            onFocus,
            onMount,
            onSearchSelected
        },
        destinationOptions,
        destinationOptions: {
            filterType,
            googlePlaceApiKey,
            googlePlaceApiUrl,
            hideSelectedFilter,
            highlightMode,
            iconName,
            id,
            matchMode,
            minLength,
            popoverDesktopMaxWidth,
            popoverDesktopMinMarginRight,
            popoverDesktopMinWidth,
            popoverDesktopWidth,
            popoverMaxDisplayCount,
            popoverMaxDisplayCountMobile,
            recentSearchesDisplayMax,
            remotePath,
            retainUserInput,
            showInlineSuggestion,
            showRecentSearches,
            showResultIcons,
            showResultLabels,
            showSpinner,
            showTrendingSuggestions,
            truncateResultTerms,
            usePopoverAnimation,
            useResultsAnimation
        },
        destinationSettings: {
            isTypeaheadInputFocus
        },
        destinationTranslations: {
            clearSearchLabel,
            noResultsFoundMessage,
            placeholderMessage,
            premiseLabel,
            premisesLabel,
            recentSearchesMessage,
            recentlyViewedLabel,
            resultsFoundMessage,
            selectedMessage,
            trendingDestinationsMessage,
            typeAirportLabel,
            typeCityLabel,
            typeLandmarkLabel,
            typeMetroStationLabel,
            typeNeighborhoodLabel,
            typePropertyLabel,
            typeRegionLabel,
            typeTrainStationLabel,
            whereLabel
        },
        latestDestinationValues,
        latestDestinationValues: {
            destination: search
        },
        datesActions: {
            setIsOpen: setDatesIsOpen
        },
        guestsActions: {
            setIsOpen: setGuestsIsOpen
        }
    } = useSearchFormContext();

    const className = classNames(fieldClassName, inputFieldClassName, {
        [inputFieldErrorClassName]: showDestinationInputFieldError(mediaSettings, formOptions, formSettings, destinationOptions, latestDestinationValues, abacusStore, systemOptions)
    }, detailsFieldClassName, detailsInputFieldClassName, destinationDetailsInputFieldClassName);

    const icon = showDestinationIcon(formOptions, destinationOptions) && (
        <SearchIcon iconName={iconName}/>
    ) || null;

    const messages = {
        clearsearch: clearSearchLabel,
        destinationplaceholdermsg: placeholderMessage,
        destinationselected: selectedMessage,
        noresultsfound: noResultsFoundMessage,
        premise: premiseLabel,
        premises: premisesLabel,
        recentlyviewed: recentlyViewedLabel,
        recentsearches: recentSearchesMessage,
        resultsfound: resultsFoundMessage,
        trendingdestinations: trendingDestinationsMessage,
        typeairport: typeAirportLabel,
        typecity: typeCityLabel,
        typelandmark: typeLandmarkLabel,
        typemetrostation: typeMetroStationLabel,
        typeneighborhood: typeNeighborhoodLabel,
        typeproperty: typePropertyLabel,
        typeregion: typeRegionLabel,
        typetrainstation: typeTrainStationLabel,
        where: whereLabel
    };

    /* istanbul ignore next */
    function handleBlur(...args) {
        onBlur(...args);
    }

    function handleFocus(...args) {
        setIsTypeaheadInputFocus(true);
        setDatesIsOpen(false);
        setGuestsIsOpen(false);

        onFocus(...args);
    }

    function handleMount(...args) {
        onMount(...args);
    }

    function handleSearchSelected(...args) {
        const [
            changedValues
        ] = args;

        setAllDestinationValues(changedValues);

        // TODO: Move this function to handleBlur
        setIsTypeaheadInputFocus(false);

        if (shouldDestinationFieldAutoProgressOnSearchSelected(mediaSettings, formOptions, destinationOptions, changedValues)) {
            setTimeout(function delayedDatesOpen() {
                setDatesIsOpen(true);
            }, 300);
        }

        onSearchSelected(...args);
    }

    const result = {
        className,
        appPrefix,
        baseUrl,
        filterType,
        googlePlaceApiKey,
        googlePlaceApiUrl,
        hideSelectedFilter,
        highlightMode,
        icon,
        id,
        isMobile,
        isTypeaheadInputFocus,
        locale,
        matchMode,
        messages,
        minLength,
        onBlur: handleBlur,
        onFocus: handleFocus,
        onMount: handleMount,
        onSearchSelected: handleSearchSelected,
        popoverDesktopMaxWidth,
        popoverDesktopMinMarginRight,
        popoverDesktopMinWidth,
        popoverDesktopWidth,
        popoverMaxDisplayCount,
        popoverMaxDisplayCountMobile,
        recentSearchesDisplayMax,
        remotePath,
        retainUserInput,
        search,
        showInlineSuggestion,
        showRecentSearches,
        showResultIcons,
        showResultLabels,
        showSpinner,
        showTrendingSuggestions,
        site,
        truncateResultTerms,
        usePopoverAnimation,
        useResultsAnimation
    };

    useDebugValue(result);

    return result;
}

export {useComponent};
